import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FitnessCenter as FitnessCenterIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';
import { Routine, Exercise, FetchWithAuth } from '../../types';
import { CreateRoutineDialog } from './CreateRoutineDialog';
import { EditRoutineDialog } from './EditRoutineDialog';
import { ImportWorkoutDialog } from './ImportWorkoutDialog';
import ShareRoutineDialog from './ShareRoutineDialog';
import SharedRoutinesView from './SharedRoutinesView';
import { API_URL } from '../../utils';
import { commonDialogStyles } from '../common/dialogStyles';

interface RoutinesViewProps {
  fetchWithAuth?: FetchWithAuth;
  onServiceUnavailable?: () => void;
  notificationRefresh?: (() => Promise<void>) | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`routines-tabpanel-${index}`}
      aria-labelledby={`routines-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const RoutinesView: React.FC<RoutinesViewProps> = ({ 
  fetchWithAuth, 
  onServiceUnavailable,
  notificationRefresh
}) => {
  const { t } = useLanguage();
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [exercisesLoading, setExercisesLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRoutine, setSelectedRoutine] = useState<Routine | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [routineToDelete, setRoutineToDelete] = useState<Routine | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importWorkoutDialogOpen, setImportWorkoutDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [routineToShare, setRoutineToShare] = useState<Routine | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Mark routine notifications as read when component mounts
  const markRoutineNotificationsAsRead = useCallback(async () => {
    if (!fetchWithAuth) return;
    
    try {
      await fetchWithAuth(`${API_URL}/notifications/mark-read-by-type`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'routines' }),
      });
      // Don't show success message as this is automatic behavior
      
      // Refresh notification badge with delay to prevent rapid calls
      if (notificationRefresh && typeof notificationRefresh === 'function') {
        setTimeout(() => {
          try {
            notificationRefresh();
          } catch (error) {
            console.error('Error calling notification refresh:', error);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error marking routine notifications as read:', error);
      // Fail silently for better UX
    }
  }, [fetchWithAuth, notificationRefresh]);

  // Load routines from API
  const loadRoutines = useCallback(async () => {
    if (!fetchWithAuth) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(`${API_URL}/routines`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch routines: ${response.status}`);
      }
      
      const data = await response.json();
      setRoutines(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error fetching routines:', err);
      setError(t('routines.loadError'));
      
      if (err.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      }
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, t, onServiceUnavailable]);

  // Load exercises from API
  const loadExercises = useCallback(async () => {
    if (!fetchWithAuth) {
      setExercisesLoading(false);
      return;
    }

    try {
      setExercisesLoading(true);
      const response = await fetchWithAuth(`${API_URL}/exercises`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch exercises: ${response.status}`);
      }
      
      const data = await response.json();
      setExercises(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error fetching exercises:', err);
      
      if (err.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      }
    } finally {
      setExercisesLoading(false);
    }
  }, [fetchWithAuth, onServiceUnavailable]);

  useEffect(() => {
    loadRoutines();
    loadExercises();
    markRoutineNotificationsAsRead();
  }, [loadRoutines, loadExercises, markRoutineNotificationsAsRead]);

  const handleCreateRoutine = async (routine: Routine) => {
    if (!fetchWithAuth) {
      setAlert({ type: 'error', message: 'API not available' });
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_URL}/routines`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(routine),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create routine');
      }

      const newRoutine = await response.json();
      setRoutines([newRoutine, ...routines]);
      setCreateDialogOpen(false);
      setAlert({ type: 'success', message: t('routines.saveSuccess') });
    } catch (err: any) {
      console.error('Error creating routine:', err);
      setAlert({ type: 'error', message: err.message || t('routines.saveError') });
      
      if (err.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      }
    }
  };

  const handleEditRoutine = async (routine: Routine) => {
    if (!fetchWithAuth || !routine.id) {
      setAlert({ type: 'error', message: 'API not available' });
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_URL}/routines/${routine.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(routine),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update routine');
      }

      const updatedRoutine = await response.json();
      setRoutines(routines.map(r => r.id === routine.id ? updatedRoutine : r));
      setEditDialogOpen(false);
      setSelectedRoutine(null);
      setAlert({ type: 'success', message: t('routines.saveSuccess') });
    } catch (err: any) {
      console.error('Error updating routine:', err);
      setAlert({ type: 'error', message: err.message || t('routines.saveError') });
      
      if (err.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      }
    }
  };

  const handleDeleteRoutine = async () => {
    if (!routineToDelete || !fetchWithAuth) return;

    try {
      const response = await fetchWithAuth(`${API_URL}/routines/${routineToDelete.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setRoutines(routines.filter(r => r.id !== routineToDelete.id));
        setAlert({
          type: 'success',
          message: t('routines.deleteSuccess')
        });
      } else {
        throw new Error('Failed to delete routine');
      }
    } catch (error: any) {
      console.error('Error deleting routine:', error);
      if (error.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      } else {
        setError(t('routines.deleteError'));
      }
    } finally {
      setDeleteDialogOpen(false);
      setRoutineToDelete(null);
    }
  };

  const handleCreateExercise = async (name: string): Promise<Exercise | null> => {
    if (!fetchWithAuth) {
      setAlert({ type: 'error', message: 'API not available' });
      return null;
    }

    try {
      const response = await fetchWithAuth(`${API_URL}/exercises`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name }),
      });

      if (!response.ok) {
        throw new Error('Failed to create exercise');
      }

      const newExercise = await response.json();
      setExercises([...exercises, newExercise]);
      return newExercise;
    } catch (err: any) {
      console.error('Error creating exercise:', err);
      setAlert({ type: 'error', message: err.message || 'Failed to create exercise' });
      
      if (err.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      }
      return null;
    }
  };

  const handleImportWorkoutSuccess = () => {
    loadRoutines(); // Refresh routines list
    setAlert({ type: 'success', message: t('importWorkout.importSuccess') });
  };

  const handleShareRoutine = (routine: Routine) => {
    setRoutineToShare(routine);
    setShareDialogOpen(true);
  };

  const handleShareSuccess = () => {
    setAlert({ type: 'success', message: t('routines.routineShared') });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const openEditDialog = (routine: Routine) => {
    setSelectedRoutine(routine);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (routine: Routine) => {
    setRoutineToDelete(routine);
    setDeleteDialogOpen(true);
  };

  const getTotalExercises = (routine: Routine): number => {
    return routine.days.reduce((total, day) => total + day.exercises.length, 0);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          {t('common.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
        mb: 3 
      }}>
        <Typography variant="h4" component="h1">
          {t('routines.title')}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => setImportWorkoutDialogOpen(true)}
            disabled={exercisesLoading}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {t('routines.importFromWorkout')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            disabled={exercisesLoading}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {t('routines.createRoutine')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="routines tabs">
          <Tab label={t('routines.myRoutines')} />
          <Tab label={t('routines.sharedRoutines')} />
        </Tabs>
      </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TabPanel value={tabValue} index={0}>
        {routines.length === 0 && !loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 8,
              textAlign: 'center'
            }}
          >
            <FitnessCenterIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('routines.noRoutines')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ mt: 2 }}
              disabled={exercisesLoading}
            >
              {t('routines.createRoutine')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {routines.map((routine) => (
              <Box key={routine.id} sx={{ flex: '1 1 300px', maxWidth: '400px' }}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" component="h2" sx={{ flex: 1 }}>
                        {routine.name}
                      </Typography>
                      <Chip 
                        label={routine.is_active ? t('routines.isActive') : 'Inactive'} 
                        color={routine.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    
                    {routine.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {routine.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        <Typography variant="body2">
                          {routine.days.length} {t('routines.days')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FitnessCenterIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        <Typography variant="body2">
                          {getTotalExercises(routine)} {t('routines.exercises')}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      {t('routines.createdAt')}: {formatDate(routine.created_at || '')}
                    </Typography>
                  </CardContent>
                  
                  <CardActions>
                    <Tooltip title={t('routines.shareRoutine')}>
                      <IconButton size="small" onClick={() => handleShareRoutine(routine)}>
                        <ShareIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.edit')}>
                      <IconButton size="small" onClick={() => openEditDialog(routine)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete')}>
                      <IconButton size="small" onClick={() => openDeleteDialog(routine)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {fetchWithAuth && (
          <SharedRoutinesView
            fetchWithAuth={fetchWithAuth}
            onServiceUnavailable={onServiceUnavailable}
          />
        )}
      </TabPanel>

      {/* Create Routine Dialog */}
      <CreateRoutineDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleCreateRoutine}
        exercises={exercises}
        onCreateExercise={handleCreateExercise}
        setExercises={setExercises}
      />

      {/* Edit Routine Dialog */}
      {selectedRoutine && (
        <EditRoutineDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedRoutine(null);
          }}
          onSave={handleEditRoutine}
          routine={selectedRoutine}
          exercises={exercises}
          onCreateExercise={handleCreateExercise}
          setExercises={setExercises}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={commonDialogStyles}
      >
        <DialogTitle>{t('routines.deleteRoutine')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('routines.deleteConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteRoutine} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Workout Dialog */}
      {fetchWithAuth && (
        <ImportWorkoutDialog
          open={importWorkoutDialogOpen}
          onClose={() => setImportWorkoutDialogOpen(false)}
          routines={routines}
          fetchWithAuth={fetchWithAuth}
          onImportSuccess={handleImportWorkoutSuccess}
          onServiceUnavailable={onServiceUnavailable}
        />
      )}

      {/* Share Routine Dialog */}
      {fetchWithAuth && (
        <ShareRoutineDialog
          open={shareDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
            setRoutineToShare(null);
          }}
          routine={routineToShare}
          fetchWithAuth={fetchWithAuth}
          onShareSuccess={handleShareSuccess}
        />
      )}
    </Box>
  );
}; 